import { useAllCheckpointsState } from 'screens/Admin/ProjectCheckpoints/hooks/useAllCheckpointsState'
import { useProjectStagesState } from 'screens/Admin/ProjectTypes/hooks/useProjectStagesState'
import useProjectTypesState from 'screens/Admin/ProjectTypes/hooks/useProjectTypesState'
import { IProjectStage, IProjectType } from 'screens/Admin/ProjectTypes/projectTypes.types'
import useStateWithChange from 'shared/hooks/useStateWithChange'
import { IArticleFormArticle } from '../faq.types'

interface IProps {
  defaultValues: Omit<IArticleFormArticle, 'projectTypeId' | 'projectStageId'>
}

const useFaqFormStateAndOptions = (props: IProps) => {
  const { checkpointsMap } = useAllCheckpointsState()
  const { projectStagesMap, projectStagesState } = useProjectStagesState()
  const { projectTypesMap, projectTypesState } = useProjectTypesState()

  const restoreFromCheckpoints = (checkpoints: string[]) => {
    if (checkpoints.length === 0) return { projectTypeId: null, projectStageId: null }
    const projectStage = projectStagesState.items.find(ps => 
      checkpoints.every(c => 
        ps.checkpoints.find(({ projectCheckpointId }) => c === projectCheckpointId)
      )
    )
    const projectType = projectTypesState.items.find(pt => pt.projectStages.some(ps => ps.projectStageId === projectStage?.id))
    return { projectTypeId: projectType?.id ?? null, projectStageId: projectStage?.id ?? null }
  }

  const [state, setState, handleChangeState] = useStateWithChange<IArticleFormArticle>({
    ...props.defaultValues,
    ...restoreFromCheckpoints(props.defaultValues.relatedCheckpoints)
  })

  const handleChangeProjectType = (projectTypeId: IProjectType['id']) => {
    if (projectTypeId !== state.projectTypeId) {
      setState((prev) => ({
        ...prev,
        projectTypeId,
        relatedCheckpoints: [],
        projectStageId: null,
      }))
    }
  }

  const handleChangeProjectStage = (projectStageId: IProjectStage['id'] | null) => {
    if (projectStageId !== state.projectStageId) {
      let projectTypeId = state.projectTypeId
      if (projectTypeId === null && projectStageId !== null) {
        const projectType = projectTypesState.items.find((pt) => pt.projectStages.some((ps) => ps.projectStageId === projectStageId))
        projectTypeId = projectType.id || null
      }
      setState((prev) => ({
        ...prev,
        projectTypeId,
        relatedCheckpoints: [],
        projectStageId: projectStageId,
      }))
    }
  }

  const handleChange: typeof handleChangeState = (val, name) => {
    switch (name) {
      case 'projectTypeId':
        return handleChangeProjectType(val as IProjectType['id'])
      case 'projectStageId':
        return handleChangeProjectStage(val as IProjectStage['id'])
      default:
        return handleChangeState(val, name)
    }
  }

  const checkpointsOptions = projectStagesMap[state.projectStageId]?.checkpoints?.map((c) => checkpointsMap[c.projectCheckpointId]) ?? []

  const projectStagesOptions =
    state.projectTypeId === null
      ? projectStagesState.items
      : projectTypesMap[state.projectTypeId]?.projectStages?.map((ps) => projectStagesMap[ps.projectStageId]) ?? []

  return {
    state: [state, handleChange] as const,
    checkpointsOptions,
    projectStagesOptions,
    projectTypesOptions: projectTypesState.items,
  }
}

export default useFaqFormStateAndOptions
